/**
 * useMealActions Hook
 *
 * Custom hook for meal editing operations that integrates with Zustand store.
 * Provides a clean API for components to perform meal updates, regenerations, and swaps.
 *
 * @module hooks/useMealActions
 */

import { useCallback, useMemo } from 'react';
import { useMealPlanStore } from '@/stores/mealPlanStore';
import type {
  EditMealFormData,
  AdjustMealFormData,
  SwapMealFormData,
} from '@/types/mealPlan';

/**
 * Return type for useMealActions hook
 */
export interface UseMealActionsReturn {
  /** Update meal name and nutrition */
  updateMeal: (mealId: string, data: EditMealFormData) => Promise<void>;
  /** Regenerate meal with AI guidance */
  regenerateMeal: (mealId: string, data: AdjustMealFormData) => Promise<void>;
  /** Swap meal with new AI-generated meal */
  swapMeal: (mealId: string, data: SwapMealFormData) => Promise<void>;
  /** Whether update operation is in progress */
  isUpdating: boolean;
  /** Whether regenerate operation is in progress */
  isRegenerating: boolean;
  /** Whether swap operation is in progress */
  isSwapping: boolean;
  /** Error from any operation */
  error: Error | null;
  /** Reset error state */
  resetError: () => void;
}

/**
 * Custom hook for meal editing operations
 *
 * @param mealPlanId - ID of the meal plan containing the meals
 * @returns Object with meal action methods and state
 *
 * @example
 * ```tsx
 * const { updateMeal, isUpdating, error } = useMealActions('meal-plan-123');
 *
 * const handleUpdate = async () => {
 *   try {
 *     await updateMeal('meal-456', {
 *       mealName: 'Updated Meal',
 *       nutrition: { caloriesPerServing: 500, proteinG: 20, carbsG: 60, fatG: 15 }
 *     });
 *   } catch (err) {
 *     console.error('Update failed:', err);
 *   }
 * };
 * ```
 */
export function useMealActions(mealPlanId: string): UseMealActionsReturn {
  // Get store actions (these are stable references)
  const storeUpdateMeal = useMealPlanStore((state) => state.updateMeal);
  const storeRegenerateMeal = useMealPlanStore((state) => state.regenerateMeal);
  const storeSwapMeal = useMealPlanStore((state) => state.swapMeal);
  const clearError = useMealPlanStore((state) => state.clearError);

  // Get state that changes
  const operations = useMealPlanStore((state) => state.operations);
  const errors = useMealPlanStore((state) => state.errors);

  // Memoize loading states
  const isUpdating = useMemo(
    () =>
      Object.keys(operations.updating).some(
        (key) => key.startsWith(`${mealPlanId}-`) && operations.updating[key]
      ),
    [operations.updating, mealPlanId]
  );

  const isRegenerating = useMemo(
    () =>
      Object.keys(operations.regenerating).some(
        (key) => key.startsWith(`${mealPlanId}-`) && operations.regenerating[key]
      ),
    [operations.regenerating, mealPlanId]
  );

  const isSwapping = useMemo(
    () =>
      Object.keys(operations.swapping).some(
        (key) => key.startsWith(`${mealPlanId}-`) && operations.swapping[key]
      ),
    [operations.swapping, mealPlanId]
  );

  // Get error for this meal plan (check all operation keys that start with mealPlanId)
  const error = useMemo(() => {
    const errorKeys = Object.keys(errors).filter(key => key.startsWith(`${mealPlanId}-`));
    for (const key of errorKeys) {
      if (errors[key]) {
        return errors[key];
      }
    }
    return null;
  }, [errors, mealPlanId]);

  // Memoize action functions
  const updateMeal = useCallback(
    (mealId: string, data: EditMealFormData) =>
      storeUpdateMeal(mealPlanId, mealId, data),
    [storeUpdateMeal, mealPlanId]
  );

  const regenerateMeal = useCallback(
    (mealId: string, data: AdjustMealFormData) =>
      storeRegenerateMeal(mealPlanId, mealId, data),
    [storeRegenerateMeal, mealPlanId]
  );

  const swapMeal = useCallback(
    (mealId: string, data: SwapMealFormData) =>
      storeSwapMeal(mealPlanId, mealId, data),
    [storeSwapMeal, mealPlanId]
  );

  const resetError = useCallback(() => {
    // Clear all errors for this meal plan (all operation keys starting with mealPlanId)
    const errorKeys = Object.keys(errors).filter(key => key.startsWith(`${mealPlanId}-`));
    errorKeys.forEach(key => clearError(key));
    // Also clear the mealPlanId key itself (used by fetchMealPlan)
    clearError(mealPlanId);
  }, [clearError, mealPlanId, errors]);

  return {
    updateMeal,
    regenerateMeal,
    swapMeal,
    isUpdating,
    isRegenerating,
    isSwapping,
    error,
    resetError,
  };
}